/* xm.js — Snowfall Parlour interactions for the Wife Grab Christmas theme.
   GSAP (+ScrollTrigger) is loaded as classic deferred scripts before this module; three.js is imported lazily
   only when the hero scrolls into view, WebGL works and the visitor has not asked for reduced motion.
   Everything degrades: no GSAP -> content simply shows; no WebGL -> the static photo card stays. */
(function () {
  'use strict';
  var doc = document, root = doc.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var g = window.gsap, ST = window.ScrollTrigger;
  var hasGsap = !!(g && ST) && !reduced;
  if (hasGsap) { g.registerPlugin(ST); }

  /* ---------------- Reveals ---------------- */
  function markReveals(scope) {
    var sel = [
      '.pp-section .pp-eyebrow', '.pp-section .pp-h2', '.pp-section .pp-sub', '.pp-section .pp-text', '.pp-section .pp-cta',
      '.vc-card', '.pp-gb', '.pp-faq__item', '.pp-guarantee__card', '.pp-media', '.vc-results__row', '.vc-results__kicker',
      '.vc-results__text', '.vc-results__caption', '.pp-pct', '.testimonial-card', '.collection .card-wrapper',
      '.collection-title', '.comparison-table-container', '.content-and-comparison-table .content-container',
      '.xm-count__eyebrow', '.xm-count__h', '.xm-count__text', '.xm-count__cta', '.xm-count__cell', '.xm-count__note',
      '.newsletter__wrapper > *'
    ].join(',');
    var nodes = (scope || doc).querySelectorAll(sel);
    nodes.forEach(function (n) {
      if (n.closest('.xm-hero')) return;
      if (!n.hasAttribute('data-xm-reveal')) n.setAttribute('data-xm-reveal', '');
    });
    return nodes;
  }
  // Reveals v2 (ported from BladeMist, Ninos 2026-10-26 "make the pop-up smoother"): no GSAP tweens, no ScrollTrigger batches.
  // Each item fully below the fold gets a CSS transition (opacity + translate3d, compositor only) and an IntersectionObserver flips
  // it on; items that cross together get a 60 ms stagger. Content on screen at boot is never hidden, so nothing blinks; the
  // transition is dropped once it has run so the hover tilt's inline transforms stay instant.
  function runReveals() {
    if (reduced || !('IntersectionObserver' in window)) return;
    var vh = window.innerHeight || 800, list = [];
    markReveals().forEach(function (n) {
      if (n.getBoundingClientRect().top > vh) list.push(n); else n.removeAttribute('data-xm-reveal');
    });
    if (!list.length) return;
    root.classList.add('xm-reveal');
    var io = new IntersectionObserver(function (entries) {
      var k = 0;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target; io.unobserve(n);
        n.style.transitionDelay = Math.min(k++, 8) * 60 + 'ms';
        n.addEventListener('transitionend', function done() { n.classList.add('xm-done'); n.style.transitionDelay = ''; n.removeEventListener('transitionend', done); });
        n.classList.add('xm-in');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    list.forEach(function (n) { io.observe(n); });
    setTimeout(function () { list.forEach(function (n) { if (!n.classList.contains('xm-in')) { n.classList.add('xm-in', 'xm-done'); } }); }, 8000);
  }

  /* ---------------- Percent rings count up ---------------- */
  function runRings() {
    if (!hasGsap) return;
    doc.querySelectorAll('.pp-pct__ring').forEach(function (ring) {
      var span = ring.querySelector('span'); if (!span) return;
      var target = parseFloat(ring.style.getPropertyValue('--pp-value')) || parseFloat(span.textContent) || 0;
      var obj = { v: 0 };
      ring.style.setProperty('--pp-value', '0'); span.textContent = '0%';
      ST.create({
        trigger: ring, start: 'top 90%', once: true,
        onEnter: function () {
          g.to(obj, { v: target, duration: 1.6, ease: 'power2.out', onUpdate: function () {
            ring.style.setProperty('--pp-value', obj.v.toFixed(1)); span.textContent = Math.round(obj.v) + '%';
          } });
        }
      });
    });
  }

  /* ---------------- Tilt + magnetic (fine pointers only) ---------------- */
  function runTilt() {
    if (!hasGsap || !finePointer) return;
    doc.querySelectorAll('.collection .card-wrapper .card, .vc-card, .pp-gb, .xm-hero__metric').forEach(function (el) {
      el.classList.add('xm-tilt');
      var rx = g.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
      var ry = g.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
      g.set(el, { transformPerspective: 900 });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        rx(-py * 7); ry(px * 9);
      });
      el.addEventListener('pointerleave', function () { rx(0); ry(0); });
    });
    doc.querySelectorAll('.button--primary, .xm-hero__actions .button').forEach(function (b) {
      var x = g.quickTo(b, 'x', { duration: 0.4, ease: 'power3' }), y = g.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        x(((e.clientX - r.left) / r.width - 0.5) * 10); y(((e.clientY - r.top) / r.height - 0.5) * 8);
      });
      b.addEventListener('pointerleave', function () { x(0); y(0); });
    });
  }

  /* ---------------- Image parallax ---------------- */
  function runParallax() {
    if (!hasGsap) return;
    doc.querySelectorAll('.pp-media__img').forEach(function (img) {
      g.fromTo(img, { yPercent: -5, scale: 1.08 }, { yPercent: 5, scale: 1.08, ease: 'none',
        scrollTrigger: { trigger: img, start: 'top bottom', end: 'bottom top', scrub: 0.6 } });
    });
  }

  /* ---------------- Countdown ---------------- */
  function runCountdown() {
    doc.querySelectorAll('[data-xm-countdown]').forEach(function (box) {
      var month = parseInt(box.getAttribute('data-month'), 10) || 12;
      var day = parseInt(box.getAttribute('data-day'), 10) || 25;
      var cells = { d: box.querySelector('[data-unit=d] .xm-count__num > span'), h: box.querySelector('[data-unit=h] .xm-count__num > span'), m: box.querySelector('[data-unit=m] .xm-count__num > span'), s: box.querySelector('[data-unit=s] .xm-count__num > span') };
      var heading = box.querySelector('[data-xm-heading]');
      var before = heading ? heading.getAttribute('data-before') : '', after = heading ? heading.getAttribute('data-after') : '';
      var last = {};
      function target() {
        var now = new Date(), y = now.getFullYear();
        var t = new Date(y, month - 1, day, 0, 0, 0);
        var end = new Date(y, month - 1, day, 23, 59, 59);
        if (now > end) t = new Date(y + 1, month - 1, day, 0, 0, 0);
        return { t: t, isDay: now >= t && now <= end };
      }
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      function set(key, val) {
        var el = cells[key]; if (!el || last[key] === val) return;
        last[key] = val;
        if (hasGsap && last.init) {
          g.fromTo(el, { yPercent: -60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.45, ease: 'power3.out', onStart: function () { el.textContent = val; } });
        } else { el.textContent = val; }
      }
      function tick() {
        var tg = target(), diff = Math.max(0, tg.t - new Date());
        var d = Math.floor(diff / 864e5), h = Math.floor(diff / 36e5) % 24, m = Math.floor(diff / 6e4) % 60, s = Math.floor(diff / 1e3) % 60;
        set('d', String(d)); set('h', pad(h)); set('m', pad(m)); set('s', pad(s));
        if (heading) heading.textContent = tg.isDay ? after : before.replace('[days]', d === 1 ? '1 Day' : d + ' Days');
        last.init = true;
      }
      tick(); setInterval(tick, 1000);
    });
  }

  /* ---------------- Hero copy ---------------- */
  function runHeroCopy(hero) {
    var h = hero.querySelector('.xm-hero__h');
    if (h && !h.querySelector('.xm-w')) {
      // Wrap words for a staggered rise; <em> words keep their tag.
      h.innerHTML = h.innerHTML.replace(/(<em>.*?<\/em>|[^\s<]+)(\s*)/g, function (_, w, sp) { return '<span class="xm-w">' + w + (sp ? ' ' : '') + '</span>'; });
    }
    if (!hasGsap) return;
    var tl = g.timeline({ defaults: { ease: 'power3.out' } });
    tl.from(hero.querySelectorAll('.pp-eyebrow'), { y: 16, opacity: 0, duration: 0.7 }, 0.05)
      .from(hero.querySelectorAll('.xm-w'), { yPercent: 70, opacity: 0, duration: 0.9, stagger: 0.06 }, 0.1)
      .from(hero.querySelectorAll('.xm-hero__lead'), { y: 18, opacity: 0, duration: 0.8 }, 0.5)
      .from(hero.querySelectorAll('.xm-hero__actions .button'), { y: 14, opacity: 0, duration: 0.6, stagger: 0.1 }, 0.65)
      .from(hero.querySelectorAll('.xm-hero__metric'), { y: 18, opacity: 0, duration: 0.7, stagger: 0.08 }, 0.8)
      .from(hero.querySelectorAll('.xm-hero__chip, .xm-hero__tag'), { scale: 0.6, opacity: 0, duration: 0.6, ease: 'back.out(2)' }, 0.9);
  }

  /* ---------------- Hero: three.js gift box ---------------- */
  function webglOk() {
    try { var c = doc.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
  }
  function initHero3D(hero) {
    var canvas = hero.querySelector('.xm-hero__canvas');
    if (!canvas || reduced || !webglOk()) return;
    var src = hero.getAttribute('data-three') || 'https://cdnjs.cloudflare.com/ajax/libs/three.js/0.160.0/three.module.min.js';
    loadThree(src).then(function (THREE) { buildHero(THREE, hero, canvas); }).catch(function (err) { if (window.console) console.warn('xm hero 3d skipped', err); });
  }
  // Shopify minifies theme JS and rewrites a dynamic import() into require(), so the ES module is pulled in by an
  // inline <script type="module"> instead and handed over on window.
  function loadThree(src) {
    return new Promise(function (resolve, reject) {
      if (window.__xmThree) return resolve(window.__xmThree);
      window.addEventListener('xm:three', function () { resolve(window.__xmThree); }, { once: true });
      var s = doc.createElement('script'); s.type = 'module';
      s.textContent = "import * as T from '" + src + "'; window.__xmThree = T; window.dispatchEvent(new Event('xm:three'));";
      doc.head.appendChild(s);
      setTimeout(function () { if (!window.__xmThree) reject(new Error('three.js did not load')); }, 15000);
    });
  }
  function buildHero(THREE, hero, canvas) {
    var stage = hero.querySelector('.xm-hero__stage');
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 2.4, 8.0); camera.lookAt(0, 1.0, 0);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xd8e6dd, 1.25));
    var key = new THREE.DirectionalLight(0xfff3e0, 2.2); key.position.set(3.5, 7, 5); scene.add(key);
    var warm = new THREE.PointLight(0xffd27a, 18, 20, 2); warm.position.set(-4, 4, 4); scene.add(warm);
    var rim = new THREE.DirectionalLight(0xcfe6ff, 0.9); rim.position.set(-3, 3, -5); scene.add(rim);

    var red = new THREE.MeshStandardMaterial({ color: 0xc8102e, roughness: 0.48, metalness: 0.06 });
    var redDeep = new THREE.MeshStandardMaterial({ color: 0xa30d25, roughness: 0.5, metalness: 0.06 });
    var gold = new THREE.MeshStandardMaterial({ color: 0xd9a441, roughness: 0.32, metalness: 0.62 });
    var white = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, metalness: 0 });

    var group = new THREE.Group(); scene.add(group);
    // Base
    var W = 2.3, H = 1.55;
    var base = new THREE.Mesh(new THREE.BoxGeometry(W, H, W), red); base.position.y = H / 2; group.add(base);
    var rb1 = new THREE.Mesh(new THREE.BoxGeometry(W + 0.06, H + 0.02, 0.36), gold); rb1.position.y = H / 2; group.add(rb1);
    var rb2 = new THREE.Mesh(new THREE.BoxGeometry(0.36, H + 0.02, W + 0.06), gold); rb2.position.y = H / 2; group.add(rb2);
    // Lid, hinged on the back edge
    var lidPivot = new THREE.Group(); lidPivot.position.set(0, H, -W / 2 - 0.08); group.add(lidPivot);
    var lidW = W + 0.16, lidH = 0.36;
    var lid = new THREE.Mesh(new THREE.BoxGeometry(lidW, lidH, lidW), redDeep); lid.position.set(0, lidH / 2, lidW / 2); lidPivot.add(lid);
    var lb1 = new THREE.Mesh(new THREE.BoxGeometry(lidW + 0.04, lidH + 0.03, 0.36), gold); lb1.position.copy(lid.position); lidPivot.add(lb1);
    var lb2 = new THREE.Mesh(new THREE.BoxGeometry(0.36, lidH + 0.03, lidW + 0.04), gold); lb2.position.copy(lid.position); lidPivot.add(lb2);
    // Bow
    var bow = new THREE.Group(); bow.position.set(0, lidH + 0.08, lidW / 2); lidPivot.add(bow);
    var loopGeo = new THREE.TorusGeometry(0.34, 0.085, 14, 36);
    var loopA = new THREE.Mesh(loopGeo, gold); loopA.rotation.y = Math.PI / 2; loopA.rotation.z = 0.35; loopA.position.x = -0.3; loopA.scale.set(1, 0.65, 1); bow.add(loopA);
    var loopB = new THREE.Mesh(loopGeo, gold); loopB.rotation.y = Math.PI / 2; loopB.rotation.z = -0.35; loopB.position.x = 0.3; loopB.scale.set(1, 0.65, 1); bow.add(loopB);
    var knot = new THREE.Mesh(new THREE.SphereGeometry(0.15, 20, 16), gold); bow.add(knot);
    // Gift tag (white card on a gold string) hanging off the front ribbon
    var tag = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.36, 0.03), white); tag.position.set(0.62, H * 0.62, W / 2 + 0.2); tag.rotation.z = -0.18; group.add(tag);
    // Product card rising from the box
    var cardGroup = new THREE.Group(); cardGroup.position.set(0, 0.35, 0.1); cardGroup.scale.setScalar(0.5); group.add(cardGroup);
    var backing = new THREE.Mesh(new THREE.PlaneGeometry(1.86, 2.3), white); backing.position.z = -0.012; cardGroup.add(backing);
    var backing2 = backing.clone(); backing2.rotation.y = Math.PI; backing2.position.z = -0.013; cardGroup.add(backing2);
    var photoMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
    var photo = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 2.14), photoMat); cardGroup.add(photo);
    var texUrl = hero.getAttribute('data-texture');
    if (texUrl) {
      var loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
      loader.load(texUrl, function (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy()); photoMat.map = t; photoMat.needsUpdate = true; photoMat.opacity = 1; });
    }
    // Floor shadow
    var shadow = new THREE.Mesh(new THREE.CircleGeometry(1.9, 48), new THREE.MeshBasicMaterial({ color: 0x14221c, transparent: true, opacity: 0.14 }));
    shadow.rotation.x = -Math.PI / 2; shadow.position.y = 0.005; shadow.scale.set(1.15, 0.8, 1); scene.add(shadow);
    // Snow
    var N = isMobile ? 260 : 700, pos = new Float32Array(N * 3), vel = new Float32Array(N * 2);
    var SX = 14, SY = 10, SZ = 8;
    for (var i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * SX; pos[i * 3 + 1] = Math.random() * SY - 2; pos[i * 3 + 2] = (Math.random() - 0.5) * SZ - 1;
      vel[i * 2] = 0.008 + Math.random() * 0.016; vel[i * 2 + 1] = Math.random() * Math.PI * 2;
    }
    var snowGeo = new THREE.BufferGeometry(); snowGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    var sc = doc.createElement('canvas'); sc.width = sc.height = 64; var sx = sc.getContext('2d');
    // White flake with a faint evergreen halo so it still reads on the cream paper behind the canvas
    var halo = sx.createRadialGradient(32, 32, 8, 32, 32, 31); halo.addColorStop(0, 'rgba(15,77,58,0.22)'); halo.addColorStop(1, 'rgba(15,77,58,0)');
    sx.fillStyle = halo; sx.fillRect(0, 0, 64, 64);
    var grd = sx.createRadialGradient(32, 32, 1, 32, 32, 13); grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.6, 'rgba(255,255,255,0.95)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    sx.fillStyle = grd; sx.fillRect(0, 0, 64, 64);
    var snowTex = new THREE.CanvasTexture(sc);
    var snow = new THREE.Points(snowGeo, new THREE.PointsMaterial({ size: isMobile ? 0.2 : 0.24, map: snowTex, transparent: true, depthWrite: false, opacity: 1, sizeAttenuation: true }));
    scene.add(snow);

    // State
    var st = { rotY: 0, dragY: 0, targetY: 0, px: 0, py: 0, open: 0, hover: 0, running: true, visible: true, scroll: 0, t: 0 };
    group.rotation.y = -0.35;
    group.scale.setScalar(0.001);
    lidPivot.rotation.x = 0;
    hero.classList.add('xm-hero--3d');

    function resize() {
      var w = stage.clientWidth || 600, h = stage.clientHeight || w;
      renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    }
    resize();
    if (window.ResizeObserver) new ResizeObserver(resize).observe(stage); else window.addEventListener('resize', resize);

    // Intro + open/close via GSAP when present, else instant
    var tl = null;
    function setOpen(v) {
      lidPivot.rotation.x = -2.05 * v;
      cardGroup.position.y = 0.35 + 2.1 * v; cardGroup.scale.setScalar(0.5 + 0.62 * v); cardGroup.rotation.x = -0.12 * v;
      bow.rotation.x = 0.0; st.open = v;
    }
    var opened = false;
    function openBox() { if (opened) return; opened = true; animateOpen(1); }
    function toggleBox() { opened = !opened; animateOpen(opened ? 1 : 0); }
    var openObj = { v: 0 };
    function animateOpen(to) {
      if (hasGsap) { g.to(openObj, { v: to, duration: to ? 1.3 : 0.8, ease: to ? 'elastic.out(1, 0.6)' : 'power3.inOut', onUpdate: function () { setOpen(openObj.v); } }); }
      else { openObj.v = to; setOpen(to); }
    }
    if (hasGsap) {
      var sObj = { s: 0.001 };
      tl = g.timeline();
      tl.to(sObj, { s: 1, duration: 1.1, ease: 'back.out(1.6)', onUpdate: function () { group.scale.setScalar(sObj.s); } }, 0.15)
        .add(openBox, 1.0);
      // Scroll: scrubbed drift and spin as the hero leaves the viewport
      ST.create({ trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.8, onUpdate: function (self) { st.scroll = self.progress; } });
    } else { group.scale.setScalar(1); setTimeout(openBox, 600); }

    // Pointer: parallax, drag to spin, tap/click to open or close
    var dragging = false, moved = 0, lastX = 0;
    window.addEventListener('pointermove', function (e) {
      st.px = (e.clientX / window.innerWidth - 0.5) * 2; st.py = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
    canvas.addEventListener('pointerdown', function (e) { dragging = true; moved = 0; lastX = e.clientX; if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return; var dx = e.clientX - lastX; lastX = e.clientX; moved += Math.abs(dx); st.dragY += dx * 0.012;
    });
    function up(e) { if (!dragging) return; dragging = false; if (moved < 6) toggleBox(); }
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('pointerenter', function () { st.hover = 1; }); canvas.addEventListener('pointerleave', function () { st.hover = 0; });

    // Pause when off-screen or tab hidden
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { st.visible = en[0].isIntersecting; }, { threshold: 0.25 }).observe(stage);
    doc.addEventListener('visibilitychange', function () { st.running = !doc.hidden; });

    var clock = new THREE.Clock();
    function frame() {
      requestAnimationFrame(frame);
      if (!st.running || !st.visible) return;
      var dt = Math.min(clock.getDelta(), 0.05); st.t += dt;
      // Snow
      var p = snowGeo.attributes.position.array;
      for (var i = 0; i < N; i++) {
        var yi = i * 3 + 1; p[yi] -= vel[i * 2] * (60 * dt); p[i * 3] += Math.sin(st.t * 0.9 + vel[i * 2 + 1]) * 0.004 * (60 * dt);
        if (p[yi] < -2.2) { p[yi] = SY - 2; p[i * 3] = (Math.random() - 0.5) * SX; }
      }
      snowGeo.attributes.position.needsUpdate = true;
      // Box: idle float + pointer tilt + drag + scroll spin
      st.targetY = -0.35 + st.dragY + st.px * 0.25 + st.scroll * 1.4;
      st.rotY += (st.targetY - st.rotY) * 0.08;
      group.rotation.y = st.rotY;
      group.rotation.x = st.py * 0.06;
      var floatY = Math.sin(st.t * 1.4) * 0.06 - st.scroll * 1.6;
      group.position.y = floatY; shadow.position.y = 0.005 - st.scroll * 1.6; shadow.scale.setScalar(1.15 - Math.sin(st.t * 1.4) * 0.03);
      shadow.scale.y = 0.8;
      cardGroup.rotation.y = Math.sin(st.t * 0.8) * 0.06 - st.rotY * 0.0; // card faces the box front; the group spin carries it
      bow.rotation.z = Math.sin(st.t * 2.2) * 0.04;
      tag.rotation.z = -0.18 + Math.sin(st.t * 1.8) * 0.08;
      camera.position.x += ((st.px * 0.5) - camera.position.x) * 0.05;
      camera.position.y += ((2.4 + st.py * -0.3) - camera.position.y) * 0.05;
      camera.lookAt(0, 1.0 + st.open * 0.7, 0);
      renderer.render(scene, camera);
    }
    frame();
  }

  function initHero() {
    doc.querySelectorAll('[data-xm-hero]').forEach(function (hero) {
      runHeroCopy(hero);
      if (reduced) return;
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); initHero3D(hero); } }, { rootMargin: '200px' });
        io.observe(hero);
      } else initHero3D(hero);
    });
  }

  // runParallax stays OFF: a scroll-scrubbed transform on the big photos was the frame cost behind the stutter.
  function boot() {
    initHero(); runCountdown(); runReveals(); runRings(); runTilt();
    if (hasGsap) { setTimeout(function () { ST.refresh(); }, 600); window.addEventListener('load', function () { ST.refresh(); }); if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () { ST.refresh(); }); }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  // Theme editor: sections reload in place
  doc.addEventListener('shopify:section:load', function () { runReveals(); initHero(); runCountdown(); if (hasGsap) ST.refresh(); });
})();
